import { useState, useEffect, useMemo, type Key } from 'react';
import { Table, message, Button, Modal, Tooltip } from 'antd';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import { ContentCard, useBoards, getBoardColumnsWithActions, useAssignBoardRole, type Board, ConferenceRoleModal } from '../../component/admin';
import BoardRoleModal from '../../component/admin/BoardRoleModal';
import {
    addCategoryPermission,
    removeCategoryPermission,
    togglePermission,
    getCategoryPermissionStatus,
    getBoardPerRoles,
    assignBoardRoles,
} from '../../api/adminAPI';
import { getLoginUser } from '../../api/user';
import { newGetCategory } from '../../api/categoryAPI';
import { getBoardHeaderInfo } from '../../component/admin/config/boardHeaderStructure';

const getSubCategoryIds = async (categoryId: number): Promise<number[]> => {
    try {
        const subCategories = await newGetCategory(categoryId);
        if (!subCategories || subCategories.length === 0) return [];

        let ids: number[] = [];
        for (const category of subCategories) {
            const subId = category.id || category.categoryId || category.category_id;
            ids.push(subId);
            const childIds = await getSubCategoryIds(subId);
            ids = [...ids, ...childIds];
        }
        return ids;
    } catch (error) {
        console.error('Failed to fetch subcategories:', error);
        return [];
    }
};

const BoardManagePage = () => {
    const { boards, loading, error, refetch } = useBoards();
    const { assignRole, loading: assignLoading } = useAssignBoardRole();
    const [selectedBoard, setSelectedBoard] = useState<Board | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConferenceModalOpen, setIsConferenceModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isHumintecUser, setIsHumintecUser] = useState<boolean>(false);
    const [isReconciling, setIsReconciling] = useState(false);

    const handleManageRoles = (board: Board) => {
        setSelectedBoard(board);
        setIsModalOpen(true);
    };

    const handleManageConferenceRoles = (board: Board) => {
        setSelectedBoard(board);
        setIsConferenceModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedBoard(null);
    };

    const handleConferenceModalClose = () => {
        setIsConferenceModalOpen(false);
        setSelectedBoard(null);
    };

    const handleTogglePermission = async (board: Board, checked: boolean) => {
        try {
            await togglePermission(board.board_id, !board.isCategoryPermissionEnabled);
            message.success(`'${board.name}'의 카테고리 별 권한이 ${checked ? '활성화' : '비활성화'} 되었습니다.`);
            refetch();
        } catch (error) {
            console.error('Toggle permission failed:', error);
            message.error('권한 설정 변경 중 오류가 발생했습니다.');
        }
    };

    const handleRoleSubmit = async (updates: Array<{ boardId: number, userId: number; added: number[]; removed: number[]; remaining: number[] }>) => {
        setIsSubmitting(true);
        try {
            const promises: Promise<any>[] = [];

            for (const update of updates) {
                // 게시판 목록은 카테고리 권한이 아니라 게시판 역할(TOTAL) 기준으로 필터링되므로,
                // 카테고리 권한과 게시판 역할을 연동한다:
                //  - 남은 카테고리가 없으면 게시판 역할도 회수(빈 배열 = 역할 해제)
                //  - 카테고리가 하나라도 있으면 TOTAL 부여
                promises.push(
                    assignRole({
                        boardId: update.boardId,
                        userId: update.userId,
                        boardRoles: update.remaining.length === 0 ? [] : [ "TOTAL" ],
                    })
                );

                const expandedAdded = new Set<number>(update.added);
                for (const categoryId of update.added) {
                    const subIds = await getSubCategoryIds(categoryId);
                    subIds.forEach(id => expandedAdded.add(id));
                }

                const expandedRemoved = new Set<number>(update.removed);
                for (const categoryId of update.removed) {
                    const subIds = await getSubCategoryIds(categoryId);
                    subIds.forEach(id => expandedRemoved.add(id));
                }

                expandedAdded.forEach(categoryId => {
                    promises.push(addCategoryPermission(update.userId, categoryId));
                });
                
                expandedRemoved.forEach(categoryId => {
                    promises.push(removeCategoryPermission(update.userId, categoryId));
                });
            }

            await Promise.all(promises);
            message.success('권한이 성공적으로 업데이트되었습니다.');
            refetch();
            handleModalClose();
        } catch (error) {
            console.error('Role update failed:', error);
            message.error('권한 업데이트 중 오류가 발생했습니다.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleConferenceRoleSubmit = async (updates: Array<{ boardId: number; userId: number; boardRoles: string[] }>) => {
        setIsSubmitting(true);

        try {
            const promises = updates.map(update =>
                assignRole({
                    boardId: update.boardId,
                    userId: update.userId,
                    boardRoles: update.boardRoles as ("TOTAL")[] ?? [],
                })
            );

            await Promise.all(promises);

            message.success(`${updates.length}명의 권한이 업데이트되었습니다.`);

            refetch();
            handleConferenceModalClose();
        } catch (error) {
            console.error('Role update failed:', error);
            message.error('권한 업데이트 중 오류가 발생했습니다.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // [1회성 점검] 카테고리 권한 ↔ 게시판 역할(TOTAL) 정합성 검사 후 불일치 수정.
    // 규칙: 카테고리 권한이 하나라도 있으면 TOTAL 보유, 하나도 없으면 미보유.
    // 배포 초기 게시판 역할 부여 로직이 없던 시절 데이터를 일괄 보정하기 위한 도구.
    const handleReconcile = async () => {
        setIsReconciling(true);
        try {
            const targetBoards = boards.filter(
                (b) =>
                    b.isPermissionEnabled &&
                    b.isCategoryPermissionEnabled &&
                    b.rootCategories?.[0]?.id != null,
            );

            if (targetBoards.length === 0) {
                message.info('카테고리 별 권한을 사용하는 게시판이 없습니다.');
                return;
            }

            type Fix = {
                boardId: number;
                boardName: string;
                userId: number;
                userName: string;
                action: 'assign' | 'revoke';
            };
            const fixes: Fix[] = [];
            let checkedUsers = 0;

            for (const board of targetBoards) {
                const rootId = board.rootCategories[0].id as number;
                const [statusRes, rolesRes] = await Promise.all([
                    getCategoryPermissionStatus(rootId),
                    getBoardPerRoles({ boardId: board.board_id }),
                ]);

                const statusList: any[] = statusRes?.data || [];
                const rolesList: any[] = rolesRes?.data || [];
                const roleUserIds = new Set<number>(
                    rolesList
                        .filter((r) => (r.permissions?.length ?? 0) > 0)
                        .map((r) => r.userId),
                );
                const boardLabel = getBoardHeaderInfo(board.name).label;

                for (const u of statusList) {
                    if (u.role === 'ADMIN_MAIN' || u.role === 'ADMIN_SUB') continue;
                    checkedUsers++;
                    const hasCategory =
                        u.permissionStatus === 'ALL' || u.permissionStatus === 'PARTIAL';
                    const hasRole = roleUserIds.has(u.userId);

                    if (hasCategory && !hasRole) {
                        fixes.push({ boardId: board.board_id, boardName: boardLabel, userId: u.userId, userName: u.name, action: 'assign' });
                    } else if (!hasCategory && hasRole) {
                        fixes.push({ boardId: board.board_id, boardName: boardLabel, userId: u.userId, userName: u.name, action: 'revoke' });
                    }
                }
            }

            const assignCount = fixes.filter((f) => f.action === 'assign').length;
            const revokeCount = fixes.filter((f) => f.action === 'revoke').length;

            if (fixes.length === 0) {
                Modal.success({
                    title: '정합성 점검 완료',
                    content: `게시판 ${targetBoards.length}개 · 유저 ${checkedUsers}건을 검사했으며 모두 정상입니다. 수정할 항목이 없습니다.`,
                });
                return;
            }

            Modal.confirm({
                title: '정합성 점검 결과',
                width: 560,
                content: (
                    <div>
                        <p>불일치 <b>{fixes.length}</b>건을 발견했습니다.</p>
                        <ul style={{ paddingLeft: 18 }}>
                            <li>게시판 역할 <b>부여</b> 필요 (카테고리 권한 있음): <b>{assignCount}</b>건</li>
                            <li>게시판 역할 <b>회수</b> 필요 (카테고리 권한 없음): <b>{revokeCount}</b>건</li>
                        </ul>
                        <div style={{ maxHeight: 200, overflowY: 'auto', background: '#fafafa', border: '1px solid #eee', borderRadius: 6, padding: 8, margin: '8px 0' }}>
                            {fixes.slice(0, 200).map((f, i) => (
                                <div key={i} style={{ fontSize: 12, color: f.action === 'assign' ? '#1677ff' : '#cf1322' }}>
                                    [{f.action === 'assign' ? '부여' : '회수'}] {f.boardName} · {f.userName}
                                </div>
                            ))}
                            {fixes.length > 200 && (
                                <div style={{ fontSize: 12, color: '#999' }}>… 외 {fixes.length - 200}건</div>
                            )}
                        </div>
                        <p>위 내용대로 지금 수정할까요?</p>
                    </div>
                ),
                okText: '수정 실행',
                cancelText: '취소',
                onOk: async () => {
                    await Promise.all(
                        fixes.map((f) =>
                            assignBoardRoles({
                                boardId: f.boardId,
                                userId: f.userId,
                                boardRoles: (f.action === 'assign' ? ['TOTAL'] : []) as any,
                            }),
                        ),
                    );
                    message.success(`정합성 수정 완료 — 부여 ${assignCount}건 / 회수 ${revokeCount}건`);
                    refetch();
                },
            });
        } catch (error) {
            console.error('정합성 점검 실패:', error);
            message.error('정합성 점검 중 오류가 발생했습니다.');
        } finally {
            setIsReconciling(false);
        }
    };

    useEffect(() => {
        const fetchUser = async () => {
            const user = await getLoginUser();
            if (user && user.name === '휴민텍') { 
                setIsHumintecUser(true);
            }
        };

        fetchUser();
    }, []);

    const columns = getBoardColumnsWithActions({
        onManageRoles: handleManageRoles,
        onManageConferenceRoles: handleManageConferenceRoles,
        onTogglePermission: handleTogglePermission,
        isHumintecUser: isHumintecUser
    });

    // 헤더 메뉴 구조와 동일하게: 순서 정렬 + 상위그룹(회원공간)을 펼침 구조(부모-자식)로 구성
    const treeData = useMemo(() => {
        const nodes: any[] = [];
        const groups: Record<string, any> = {};

        const sorted = [...boards].sort(
            (a, b) => getBoardHeaderInfo(a.name).order - getBoardHeaderInfo(b.name).order
        );

        for (const board of sorted) {
            const info = getBoardHeaderInfo(board.name);
            if (info.parent) {
                if (!groups[info.parent]) {
                    groups[info.parent] = {
                        id: `group-${info.parent}`,
                        name: info.parent,
                        isGroup: true,
                        children: [],
                    };
                    nodes.push(groups[info.parent]);
                }
                groups[info.parent].children.push(board);
            } else {
                nodes.push(board);
            }
        }
        return nodes;
    }, [boards]);

    // 그룹 행은 기본적으로 펼쳐진 상태로 표시
    const groupKeys = useMemo(
        () => treeData.filter((n) => n.isGroup).map((n) => n.id as string),
        [treeData]
    );
    const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([]);
    useEffect(() => {
        setExpandedRowKeys(groupKeys);
    }, [groupKeys]);

    if (loading) return <div>loading...</div>;
    if (error) return <div>error: {error}</div>;

    return (
        <>
            <ContentCard title="게시판 권한 관리">
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                    <Tooltip title="카테고리 권한과 게시판 역할(TOTAL)의 정합성을 전체 검사한 뒤, 불일치(카테고리 권한은 있는데 게시판 역할이 없거나 그 반대)를 일괄 수정합니다. 정상이면 아무 것도 바꾸지 않습니다.">
                        <Button
                            icon={<SafetyCertificateOutlined />}
                            loading={isReconciling}
                            onClick={handleReconcile}
                        >
                            권한 정합성 점검 · 수정
                        </Button>
                    </Tooltip>
                </div>
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={treeData}
                    expandable={{
                        expandedRowKeys,
                        onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
                    }}
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        defaultPageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showTotal: (total) => `Total ${total}`,
                    }}
                />
            </ContentCard>

            <BoardRoleModal
                open={isModalOpen}
                board={selectedBoard}
                onClose={handleModalClose}
                onSubmit={handleRoleSubmit}
                isSubmitting={isSubmitting}
            />

            <ConferenceRoleModal
                open={isConferenceModalOpen}
                board={selectedBoard}
                onClose={handleConferenceModalClose}
                onSubmit={handleConferenceRoleSubmit}
                isSubmitting={isSubmitting}
            />
        </>
    );
};

export default BoardManagePage;